'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useClpCourse } from '../../../context/clp-course-provider.react-component';
import type { PreviewableLecture } from '@/types/lecture';
import type { AssetVideo } from '@/generated/graphql';
import { useLectureAssetIdsQuery, usePreviewableAssetQuery } from '@/generated/graphql';
import { usePreviewableLectures } from './use-previewable-lectures';

/**
 * Returns an ordered list of previewable items — promo video first (if available), then curriculum
 * lectures. The asset field on each item is populated once the LectureAssetIds query resolves;
 * it will be undefined for curriculum lectures until then.
 *
 * durationInSeconds is derived from the lecture curriculum data, not the asset.
 */
export function usePreviewableAssets() {
  const $ = _c(24);
  const course = useClpCourse();
  const previewableLectures = usePreviewableLectures();
  let t0;
  if ($[0] !== previewableLectures) {
    t0 = previewableLectures.map(_temp);
    $[0] = previewableLectures;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const lectureIds = t0;
  let t1;
  if ($[2] !== lectureIds) {
    t1 = {
      ids: lectureIds
    };
    $[2] = lectureIds;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = lectureIds.length > 0;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      enabled: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: lectureAssetData
  } = useLectureAssetIdsQuery(t1, t3);
  const promoAssetId = course.promoVideoAsset?.id ?? "";
  let t4;
  if ($[6] !== promoAssetId) {
    t4 = {
      assetId: promoAssetId
    };
    $[6] = promoAssetId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !!promoAssetId;
  let t6;
  if ($[8] !== t5) {
    t6 = {
      enabled: t5
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: promoAssetData
  } = usePreviewableAssetQuery(t4, t6);
  const freshPromoAsset = promoAssetData?.asset?.__typename === "AssetVideo" ? promoAssetData.asset as AssetVideo : undefined;
  let assetMap;
  let thumbnailMap;
  if ($[10] !== lectureAssetData?.lectures) {
    assetMap = new Map();
    thumbnailMap = new Map();
    lectureAssetData?.lectures?.forEach(lecture => {
      if (!lecture) {
        return;
      }
      thumbnailMap.set(lecture.id, lecture.thumbnail ?? null);
      if ((lecture.__typename === "VideoLecture" || lecture.__typename === "VideoMashupLecture") && (lecture.asset?.__typename === "AssetVideo" || lecture.asset?.__typename === "AssetVideoMashup")) {
        assetMap.set(lecture.id, lecture.asset as AssetVideo);
      }
    });
    $[10] = lectureAssetData?.lectures;
    $[11] = assetMap;
    $[12] = thumbnailMap;
  } else {
    assetMap = $[11];
    thumbnailMap = $[12];
  }
  let t7;
  if ($[13] !== assetMap || $[14] !== thumbnailMap) {
    t7 = {
      lectureAssetMap: assetMap,
      lectureThumbnailMap: thumbnailMap
    };
    $[13] = assetMap;
    $[14] = thumbnailMap;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    lectureAssetMap,
    lectureThumbnailMap
  } = t7;
  let items;
  if ($[16] !== course.images?.height125 || $[17] !== course.promoVideoAsset || $[18] !== course.title || $[19] !== freshPromoAsset || $[20] !== lectureAssetMap || $[21] !== lectureThumbnailMap || $[22] !== previewableLectures) {
    items = [];
    if (course.promoVideoAsset) {
      items.push({
        id: course.promoVideoAsset.id,
        title: course.title,
        durationInSeconds: Number(course.promoVideoAsset.duration) || 0,
        thumbnail: course.images?.height125 ?? null,
        asset: freshPromoAsset ?? course.promoVideoAsset
      });
    }
    previewableLectures.forEach(lecture_0 => {
      items.push({
        ...lecture_0,
        thumbnail: lectureThumbnailMap.has(lecture_0.id) ? lectureThumbnailMap.get(lecture_0.id) ?? null : lecture_0.thumbnail,
        asset: lectureAssetMap.get(lecture_0.id)
      });
    });
    $[16] = course.images?.height125;
    $[17] = course.promoVideoAsset;
    $[18] = course.title;
    $[19] = freshPromoAsset;
    $[20] = lectureAssetMap;
    $[21] = lectureThumbnailMap;
    $[22] = previewableLectures;
    $[23] = items;
  } else {
    items = $[23];
  }
  return items;
}
function _temp(l) {
  return l.id;
}